import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import { PAGES, pathFor } from "@/data/pages";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";

// Builds a flat searchable index from page titles + intros.
function buildIndex(lang, t) {
  const all = lang === "zh" ? ZH : EN;
  return PAGES.map((p) => {
    const c = all[p.id] || {};
    const title = t.pageNames[p.id] || c.h1 || "";
    const intro = c.intro || c.metaDescription || "";
    // Aggregate section headings & bullets if present
    let extra = "";
    if (Array.isArray(c.sections)) {
      extra = c.sections
        .map((s) => `${s.heading || ""} ${Array.isArray(s.body) ? s.body.join(" ") : s.body || ""} ${(s.bullets || []).join(" ")}`)
        .join(" ");
    }
    return {
      id: p.id,
      path: pathFor(p.id, lang),
      title,
      intro,
      blob: `${title} ${intro} ${extra}`.toLowerCase(),
    };
  });
}

export default function SearchOverlay({ open, onClose }) {
  const { lang, t } = useLang();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const index = useMemo(() => buildIndex(lang, t), [lang, t]);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const needle = q.trim().toLowerCase();
  const results = needle
    ? index.filter((r) => r.blob.includes(needle)).slice(0, 12)
    : index.slice(0, 8);

  return (
    <div className="fixed inset-0 z-[70] bg-white overflow-y-auto" data-testid="search-overlay">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-end">
          <button onClick={onClose} data-testid="search-close" aria-label="Close" className="p-2 hover:text-[var(--abs-orange)]">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="max-w-3xl mx-auto pt-10">
          <div className="flex items-center gap-4 border-b-2 border-[var(--abs-ink)] pb-3">
            <Search className="h-6 w-6 text-[var(--abs-orange)]" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "zh" ? "搜索服务、税务话题…" : "Search services, tax topics…"}
              data-testid="search-input"
              className="flex-1 bg-transparent outline-none serif text-3xl md:text-5xl leading-tight placeholder:text-[var(--abs-mute)]/60"
            />
          </div>

          <div className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-4">
            {needle
              ? (lang === "zh" ? `${results.length} 个结果` : `${results.length} result${results.length === 1 ? "" : "s"}`)
              : (lang === "zh" ? "热门页面" : "Popular pages")}
          </div>

          {results.length === 0 ? (
            <div className="py-10 text-[var(--abs-mute)]">
              {lang === "zh" ? "未找到匹配结果。" : "No matching results."}
            </div>
          ) : (
            <ul className="divide-y divide-[var(--abs-line)]" data-testid="search-results">
              {results.map((r) => (
                <li key={r.id}>
                  <Link
                    to={r.path}
                    onClick={onClose}
                    data-testid={`search-result-${r.id}`}
                    className="group flex items-start justify-between gap-6 py-5 hover:text-[var(--abs-orange)]"
                  >
                    <div className="min-w-0">
                      <div className="serif text-xl md:text-2xl leading-snug">{r.title}</div>
                      <div className="mt-1 text-[14px] text-[var(--abs-mute)] line-clamp-2">{r.intro}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 mt-2 shrink-0 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
